import { LicenseInfo } from '../types';

const LICENSE_KEY_STORAGE = 'ps_license_data';
const TRIAL_USED_KEY = 'ps_trial_used';
const LAST_VERIFICATION_KEY = 'ps_last_verification';
const LAST_PRINT_COUNT_SYNC_KEY = 'ps_last_print_sync';
const OFFLINE_DAYS_KEY = 'ps_offline_days';
const ACTIVATION_ATTEMPT_KEY = 'ps_activation_attempt';
const ACTIVATION_ATTEMPT_TIME_KEY = 'ps_activation_attempt_time';
const LAST_KNOWN_TIMESTAMP_KEY = 'ps_last_known_timestamp';
const TIME_TAMPER_FLAG_KEY = 'ps_time_tamper_detected';

// License server URLs - All using Firebase Functions
export const VERIFY_LICENSE_URL = 'https://us-central1-plate-license-sys.cloudfunctions.net/verifyLicense';
export const REGISTER_TRIAL_URL = 'https://us-central1-plate-license-sys.cloudfunctions.net/registerUser';
export const SEND_OTP_URL = 'https://us-central1-plate-license-sys.cloudfunctions.net/sendOTP';
export const UPGRADE_PRO_URL = 'https://us-central1-plate-license-sys.cloudfunctions.net/registerUser';
export const SYNC_PRINT_COUNT_URL = 'https://us-central1-plate-license-sys.cloudfunctions.net/syncPrintCount';
// Default API URL
export const DEFAULT_SCRIPT_URL = 'https://us-central1-plate-license-sys.cloudfunctions.net/verifyLicense';
export const DEFAULT_QR_BASE_URL = 'https://us-central1-plate-license-sys.cloudfunctions.net/verifyLicense';

export const LicenseManager = {
  // Time Travel Detection - check if system clock has been rewound
  checkTimeTamper: (): { isTampered: boolean; message?: string } => {
    const now = Date.now();
    const lastKnownTimestamp = localStorage.getItem(LAST_KNOWN_TIMESTAMP_KEY);
    const timeTamperFlag = localStorage.getItem(TIME_TAMPER_FLAG_KEY);
    
    // If already flagged as tampered, require online verification
    if (timeTamperFlag === 'true') {
      return { 
        isTampered: true, 
        message: 'System clock error detected. Please connect to the internet to verify your license.' 
      };
    }
    
    if (lastKnownTimestamp) {
      const lastTime = parseInt(lastKnownTimestamp);
      // Allow 5 minute tolerance for minor clock adjustments
      const tolerance = 5 * 60 * 1000;
      
      if (now < lastTime - tolerance) {
        // Time has gone backwards! Possible tampering
        console.warn('Time travel detected!', {
          currentTime: new Date(now).toISOString(),
          lastKnownTime: new Date(lastTime).toISOString(),
          difference: (lastTime - now) / 1000 / 60 + ' minutes'
        });
        
        // Set the tamper flag
        localStorage.setItem(TIME_TAMPER_FLAG_KEY, 'true');
        
        return { 
          isTampered: true, 
          message: 'System clock error. Please connect to the internet to verify your license.' 
        };
      }
    }
    
    // Update last known timestamp
    localStorage.setItem(LAST_KNOWN_TIMESTAMP_KEY, now.toString());
    return { isTampered: false };
  },

  // Clear time tamper flag (call this after successful online verification)
  clearTimeTamperFlag: (): void => {
    localStorage.removeItem(TIME_TAMPER_FLAG_KEY);
    localStorage.setItem(LAST_KNOWN_TIMESTAMP_KEY, Date.now().toString());
    console.log('Time tamper flag cleared after successful online verification');
  },

  // Update last known timestamp (call this periodically during app use)
  updateLastKnownTimestamp: (): void => {
    localStorage.setItem(LAST_KNOWN_TIMESTAMP_KEY, Date.now().toString());
  },

  getStoredLicense: (): LicenseInfo | null => {
    try {
      // First check for time tampering
      const tamperCheck = LicenseManager.checkTimeTamper();
      if (tamperCheck.isTampered) {
        console.warn('License access blocked due to time tampering');
        return null;
      }
      
      const data = localStorage.getItem(LICENSE_KEY_STORAGE);
      if (!data) return null;
      const parsed = JSON.parse(data);
      // Basic expiration check
      if (parsed.expiryDate < Date.now()) {
          return null; // Expired
      }
      return parsed as LicenseInfo;
    } catch (e) {
      return null;
    }
  },

  getMachineId: async (): Promise<string> => {
      // If Electron (Windows/Mac/Linux)
      if (/Electron/.test(navigator.userAgent)) {
          try {
              const { ipcRenderer } = (window as any).require('electron');
              const id = await ipcRenderer.invoke('get-machine-id');
              if (id && id !== 'FALLBACK-DEV-ID-9999') {
              return id;
              }
          } catch(e) {
              console.warn('Failed to get machine ID from Electron:', e);
          }
      }
      
      // If Capacitor (Android/iOS)
      try {
          const { Capacitor, registerPlugin } = await import('@capacitor/core');
          if (Capacitor.isNativePlatform()) {
              const DeviceId = registerPlugin('DeviceId');
              const result = await DeviceId.getDeviceId();
              if (result && result.deviceId) {
                  return result.deviceId;
              }
          }
      } catch(e) {
          // Plugin not available or not on native platform
          console.warn('DeviceId plugin not available:', e);
          }
      
      // Browser Fallback (Development only - not secure)
      // Use IndexedDB for better persistence than localStorage
      let storedId = localStorage.getItem('ps_browser_device_id');
      if(!storedId) {
          // Generate a more stable ID based on browser fingerprint
          const screenWidth = typeof window !== 'undefined' && window.screen ? window.screen.width : 0;
          const screenHeight = typeof window !== 'undefined' && window.screen ? window.screen.height : 0;
          const fingerprint = [
              navigator.userAgent,
              navigator.language,
              `${screenWidth}x${screenHeight}`,
              new Date().getTimezoneOffset()
          ].join('|');
          
          // Simple hash function
          let hash = 0;
          for (let i = 0; i < fingerprint.length; i++) {
              const char = fingerprint.charCodeAt(i);
              hash = ((hash << 5) - hash) + char;
              hash = hash & hash; // Convert to 32bit integer
          }
          
          storedId = 'BROWSER-' + Math.abs(hash).toString(36).toUpperCase().padStart(10, '0');
          localStorage.setItem('ps_browser_device_id', storedId);
      }
      return storedId;
  },

  getPlatform: async (): Promise<string> => {
      if (/Electron/.test(navigator.userAgent)) {
          try {
              const { ipcRenderer } = (window as any).require('electron');
              const platform = await ipcRenderer.invoke('get-platform');
              if (platform && platform.trim()) {
                  return platform.trim();
              }
          } catch(e) {
              console.warn('Failed to get platform from Electron:', e);
          }
      }
      
      // Fallback: detect platform from user agent or navigator
      const ua = navigator.userAgent || '';
      if (/android/i.test(ua)) return 'android';
      if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
      if (/win/i.test(ua)) return 'windows';
      if (/mac/i.test(ua)) return 'macos';
      if (/linux/i.test(ua)) return 'linux';
      
      return navigator.platform || 'unknown';
  },

  generateRegistrationQRUrl: (
    machineId: string, 
    licenseKey: string,
    details: { name: string, company: string, address: string, sid: string, email: string, phone: string, platform: string },
    baseUrl?: string
  ) => {
      const params = new URLSearchParams();
      params.append('key', licenseKey.trim());
      params.append('hwId', machineId.trim());
      params.append('name', details.name.trim());
      params.append('company', details.company.trim());
      params.append('address', details.address.trim());
      params.append('sid', details.sid.trim());
      
      // NEW CRM Fields
      params.append('email', details.email.trim());
      params.append('phone', details.phone.trim());
      params.append('platform', details.platform.trim());
      params.append('count', '0'); // Initial activation count

      // QR codes use the QR base URL, not the API URL
      const targetUrl = baseUrl && baseUrl.startsWith('http') ? baseUrl : DEFAULT_QR_BASE_URL;

      return `${targetUrl}?${params.toString()}`;
  },

  // New: Attempt direct online activation
  attemptOnlineActivation: async (
    machineId: string, 
    licenseKey: string,
    details: { name: string, company: string, address: string, sid: string, email: string, phone: string, platform: string },
    baseUrl?: string
  ): Promise<{ success: boolean, code?: string, message?: string }> => {
      if (!navigator.onLine) return { success: false, message: 'Offline' };

      const url = LicenseManager.generateRegistrationQRUrl(machineId, licenseKey, details, baseUrl);
      
      try {
          const response = await fetch(url, {
              method: 'GET',
              headers: { 'Accept': 'application/json' }
          });
          
          if (!response.ok) {
              return { success: false, message: `Server error: ${response.status}` };
          }

          const data = await response.json();
          // Assuming Firebase returns { code: "ABCD" } on success
          if (data && data.code) {
              return { success: true, code: data.code };
          }
          return { success: false, message: 'Invalid response from server' };
      } catch (e: any) {
          return { success: false, message: e.message };
      }
  },

  // Check if activation attempt is rate-limited (1 minute delay)
  checkActivationRateLimit: (): boolean => {
      const lastAttempt = localStorage.getItem(ACTIVATION_ATTEMPT_TIME_KEY);
      if (lastAttempt) {
          const timeSinceLastAttempt = Date.now() - parseInt(lastAttempt);
          const oneMinute = 60 * 1000;
          if (timeSinceLastAttempt < oneMinute) {
              const remainingSeconds = Math.ceil((oneMinute - timeSinceLastAttempt) / 1000);
              throw new Error(`Please wait ${remainingSeconds} seconds before trying again.`);
          }
      }
      return true;
  },

  recordActivationAttempt: () => {
      const attemptCount = parseInt(localStorage.getItem(ACTIVATION_ATTEMPT_KEY) || '0') + 1;
      localStorage.setItem(ACTIVATION_ATTEMPT_KEY, attemptCount.toString());
      localStorage.setItem(ACTIVATION_ATTEMPT_TIME_KEY, Date.now().toString());
  },

  validateActivationCode: async (
      activationCode: string, 
      machineId: string, 
      licenseKey: string,
      baseUrl?: string
  ): Promise<{ tier: 'BASIC' | 'PROFESSIONAL', expiryDate?: number } | false> => {
      // Check rate limit
      LicenseManager.checkActivationRateLimit();
      LicenseManager.recordActivationAttempt();
      
      // Server-side validation - call the server instead of using client secret
      if (!navigator.onLine) {
          throw new Error('Internet connection required for license activation.');
      }

      const targetUrl = baseUrl && baseUrl.startsWith('http') ? baseUrl : DEFAULT_SCRIPT_URL;
      const verifyUrl = `${targetUrl}/verify?key=${encodeURIComponent(licenseKey.trim())}&hwId=${encodeURIComponent(machineId.trim())}&code=${encodeURIComponent(activationCode.trim().toUpperCase())}`;
      
      try {
          const response = await fetch(verifyUrl, {
              method: 'GET',
              headers: { 'Accept': 'application/json' }
          });
          
          if (!response.ok) {
              return false;
          }

          const data = await response.json();
          if (data && data.tier) {
              // Calculate expiryDate from validUntil (timestamp) or validFrom + duration
              let expiryDate: number | undefined;
              if (data.validUntil) {
                  // validUntil is a timestamp (seconds or milliseconds)
                  expiryDate = typeof data.validUntil === 'number' 
                      ? (data.validUntil > 1e12 ? data.validUntil : data.validUntil * 1000) // Convert seconds to ms if needed
                      : new Date(data.validUntil).getTime();
              } else if (data.validFrom && data.duration) {
                  // Calculate from validFrom + duration (in days)
                  const validFromTime = typeof data.validFrom === 'number'
                      ? (data.validFrom > 1e12 ? data.validFrom : data.validFrom * 1000)
                      : new Date(data.validFrom).getTime();
                  expiryDate = validFromTime + (data.duration * 24 * 60 * 60 * 1000);
              } else if (data.expiryDate) {
                  expiryDate = typeof data.expiryDate === 'number'
                      ? (data.expiryDate > 1e12 ? data.expiryDate : data.expiryDate * 1000)
                      : new Date(data.expiryDate).getTime();
              }
              
              return { tier: data.tier as 'BASIC' | 'PROFESSIONAL', expiryDate };
          }
          return false;
      } catch (e: any) {
          return false;
      }
  },

  // --- TRIAL LOGIC ---
  hasUsedTrial: (): boolean => {
      return localStorage.getItem(TRIAL_USED_KEY) === 'true';
  },

  // Clear trial flag (for testing/debugging)
  clearTrialFlag: (): void => {
      localStorage.removeItem(TRIAL_USED_KEY);
      console.log('Trial flag cleared. You can now start a new trial.');
  },

  // Clear everything (license and trial flag) - for debugging
  clearAll: (): void => {
      localStorage.removeItem(LICENSE_KEY_STORAGE);
      localStorage.removeItem(LAST_VERIFICATION_KEY);
      localStorage.removeItem(LAST_PRINT_COUNT_SYNC_KEY);
      localStorage.removeItem(TRIAL_USED_KEY);
      console.log('All license data cleared.');
  },

  // Send OTP for trial verification (and pre-check eligibility)
  sendOtp: async (
      email: string, 
      machineId?: string,
      details?: { name: string, company: string, phone: string }
  ): Promise<{ success: boolean, message: string }> => {
      if (!email) throw new Error("Email is required.");
      
      const url = SEND_OTP_URL;
      
      const body: any = { email };
      if (machineId) body.hwId = machineId;
      if (details) {
          body.name = details.name;
          body.company = details.company;
          body.phone = details.phone;
      }
      
      try {
          const response = await fetch(url, {
              method: 'POST',
              headers: { 
                  'Accept': 'application/json',
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify(body)
          });
          
          const data = await response.json();
          
          if (!response.ok) {
              throw new Error(data.error || data.message || "Failed to send OTP");
          }
          
          return { success: true, message: data.message };
      } catch (e: any) {
          throw new Error(e.message || "Network error sending OTP");
      }
  },

  // Upgrade to Pro Trial
  upgradeToProTrial: async (
      license: LicenseInfo
  ): Promise<{ success: boolean, message: string, expiry?: number }> => {
      if (!navigator.onLine) {
          throw new Error("Internet connection required to upgrade.");
      }

      const url = UPGRADE_PRO_URL;
      
      try {
          const response = await fetch(url, {
              method: 'POST',
              headers: { 
                  'Accept': 'application/json',
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({ 
                  key: license.key,
                  hwId: license.hwId
              })
          });
          
          const data = await response.json();
          
          if (!response.ok) {
              throw new Error(data.error || data.message || "Failed to activate Pro trial");
          }
          
          return { 
              success: true, 
              message: "Pro trial activated successfully!",
              expiry: data.proTrialExpiry ? new Date(data.proTrialExpiry).getTime() : undefined
          };
      } catch (e: any) {
          throw new Error(e.message || "Network error activating Pro trial");
      }
  },

  activateTrial: async (
      machineId: string,
      details: { name: string, company: string, address: string, email: string, phone: string, platform: string, otp?: string },
      baseUrl?: string
  ): Promise<LicenseInfo> => {
      // Client-side check (can be bypassed - server MUST validate device ID hasn't used trial)
      const hasUsedTrial = LicenseManager.hasUsedTrial();
      console.log('Trial usage check:', { hasUsedTrial });
      
      if (hasUsedTrial) {
          // Server-side check is the authoritative source, but warn user
          console.warn("Trial may have already been used on this device - server will validate");
      }

      if (!navigator.onLine) {
          throw new Error("Internet connection required to activate trial.");
      }

      // Register trial with server - Use the registerUser endpoint with trialRequested flag
      const trialUrl = REGISTER_TRIAL_URL;
      
      if (!trialUrl || trialUrl.trim() === '') {
          throw new Error('License server URL is not configured. Please contact support.');
      }
      
      // Validate required fields are not empty
      const trimmedName = details.name?.trim() || '';
      const trimmedCompany = details.company?.trim() || '';
      const trimmedEmail = details.email?.trim() || '';
      const trimmedPhone = details.phone?.trim() || '';
      const trimmedPlatform = details.platform?.trim() || '';
      const trimmedAddress = details.address?.trim() || '';
      const trimmedMachineId = machineId?.trim() || '';
      const trimmedOtp = details.otp?.trim() || '';
      
      // Debug: Log what's missing
      const missingFields = [];
      if (!trimmedName) missingFields.push('Name');
      if (!trimmedCompany) missingFields.push('Company');
      if (!trimmedEmail) missingFields.push('Email');
      if (!trimmedPhone) missingFields.push('Phone');
      if (!trimmedPlatform) missingFields.push('Platform');
      if (!trimmedMachineId) missingFields.push('Device ID');
      // Allow "REGISTRATION_VERIFIED" as valid OTP (indicates user completed registration flow)
      if (!trimmedOtp && trimmedOtp !== 'REGISTRATION_VERIFIED') missingFields.push('Verification Code (OTP)');
      
      if (missingFields.length > 0) {
          console.error('Trial activation validation failed. Missing fields:', missingFields);
          console.error('Field values:', {
              name: trimmedName || '(empty)',
              company: trimmedCompany || '(empty)',
              email: trimmedEmail || '(empty)',
              phone: trimmedPhone || '(empty)',
              platform: trimmedPlatform || '(empty)',
              machineId: trimmedMachineId || '(empty)',
              otp: trimmedOtp || '(empty)'
          });
          throw new Error(`Missing required information: ${missingFields.join(', ')}. Please fill in all fields before starting trial.`);
      }
      
      // Try POST with body first (more standard for POST requests)
      // Get initial print count (should be 0 for new trial)
      const initialPrintCount = parseInt(localStorage.getItem('ps_print_count') || '0');
      
      const requestBody = {
          hwId: trimmedMachineId,
          name: trimmedName,
          company: trimmedCompany,
          address: trimmedAddress,
          email: trimmedEmail,
          phone: trimmedPhone,
          platform: trimmedPlatform,
          count: initialPrintCount, // Send print count during trial activation
          otp: trimmedOtp,
          trialRequested: true,
          deviceId: trimmedMachineId,
          systemInfo: {
              platform: trimmedPlatform,
              hwId: trimmedMachineId
          }
      };
      
      // Debug logging
      console.log('Trial activation request:', {
          url: trialUrl,
          body: requestBody,
          allFieldsPresent: {
              hwId: !!trimmedMachineId,
              name: !!trimmedName,
              company: !!trimmedCompany,
              address: !!trimmedAddress,
              email: !!trimmedEmail,
              phone: !!trimmedPhone,
              platform: !!trimmedPlatform,
              otp: !!trimmedOtp
          }
      });
      
      try {
          const response = await fetch(trialUrl, {
              method: 'POST',
              headers: { 
                  'Accept': 'application/json',
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify(requestBody)
          });
          
          // Check content type before parsing
          const contentType = response.headers.get('content-type') || '';
          const isJson = contentType.includes('application/json');
          
          if (!response.ok) {
              let errorMessage = `Server returned status ${response.status}`;
              let errorDetails: any = null;
              
              if (isJson) {
                  try {
                      const errorData = await response.json();
                      errorDetails = errorData;
                      errorMessage = errorData.message || errorData.error || errorMessage;
                      console.error('Trial activation server error (JSON):', errorData);
                  } catch (e) {
                      console.error('Failed to parse error JSON:', e);
                      // Fall through to text parsing
                  }
              }
              
              // If not JSON or JSON parsing failed, try text
              if (!isJson || errorMessage === `Server returned status ${response.status}`) {
                  const text = await response.text().catch(() => '');
                  console.error('Trial activation server error (text):', text);
                  if (text) {
                      // Check if it's HTML
                      if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
                          errorMessage = `Server returned HTML instead of JSON. The endpoint may not exist or the URL is incorrect. Check that ${targetUrl}/trial is a valid API endpoint.`;
                      } else {
                          // Try to extract meaningful error from text
                          const lowerText = text.toLowerCase();
                          if (lowerText.includes('missing') || lowerText.includes('required')) {
                              errorMessage = text.substring(0, 200);
                      } else {
                          errorMessage += `: ${text.substring(0, 200)}`;
                          }
                      }
                  }
              }
              throw new Error(errorMessage);
          }

          // Ensure response is JSON before parsing
          if (!isJson) {
              const text = await response.text();
              throw new Error(`Server returned ${contentType || 'non-JSON'} instead of JSON. Response: ${text.substring(0, 200)}`);
          }

          const data = await response.json();
          
          console.log('Trial activation successful! Server response:', data);
          
          // Handle validUntil (ISO string or timestamp) or expiryDate
          let expiryDate: number;
          if (data.validUntil) {
              // validUntil can be ISO string or timestamp
              expiryDate = typeof data.validUntil === 'number' 
                  ? (data.validUntil > 1e12 ? data.validUntil : data.validUntil * 1000) // Convert seconds to ms if needed
                  : new Date(data.validUntil).getTime();
          } else if (data.expiryDate) {
              expiryDate = typeof data.expiryDate === 'number'
                  ? (data.expiryDate > 1e12 ? data.expiryDate : data.expiryDate * 1000)
                  : new Date(data.expiryDate).getTime();
          } else {
              // Fallback: 7 days from now
              expiryDate = Date.now() + (7 * 24 * 60 * 60 * 1000);
          }

          console.log('Parsed expiry date:', new Date(expiryDate).toISOString());

          const trialLicense: LicenseInfo = {
              key: data.key || `TRIAL-${machineId.substring(0, 8)}`, // Use server's key if provided
              hwId: machineId,
              tier: (data.tier === 'Trial' || data.tier === 'PROFESSIONAL') ? 'PROFESSIONAL' : 'PROFESSIONAL', // Map Trial tier to PROFESSIONAL
              expiryDate: expiryDate,
              contactName: details.name,
              companyName: details.company,
              isTrial: true
          };
          
          console.log('Trial license created:', {
              key: trialLicense.key,
              expiryDate: new Date(trialLicense.expiryDate).toISOString(),
              tier: trialLicense.tier
          });

          LicenseManager.saveLicense(trialLicense);
          localStorage.setItem(TRIAL_USED_KEY, 'true');
          return trialLicense;
      } catch (e: any) {
          throw new Error(e.message || 'Failed to activate trial. Please try again.');
      }
  },

  saveLicense: (info: LicenseInfo) => {
      localStorage.setItem(LICENSE_KEY_STORAGE, JSON.stringify(info));
  },

  clear: () => {
      localStorage.removeItem(LICENSE_KEY_STORAGE);
      localStorage.removeItem(LAST_VERIFICATION_KEY);
      localStorage.removeItem(LAST_PRINT_COUNT_SYNC_KEY);
      localStorage.removeItem(OFFLINE_DAYS_KEY);
      localStorage.removeItem(ACTIVATION_ATTEMPT_KEY);
      localStorage.removeItem(ACTIVATION_ATTEMPT_TIME_KEY);
  },

  // Daily license verification
  verifyLicenseWithServer: async (
      license: LicenseInfo,
      baseUrl?: string
  ): Promise<{ valid: boolean, revoked?: boolean, expired?: boolean, message?: string, expiryDate?: number }> => {
      if (!navigator.onLine) {
          // Track offline days
          const lastVerification = localStorage.getItem(LAST_VERIFICATION_KEY);
          if (lastVerification) {
              const daysOffline = Math.floor((Date.now() - parseInt(lastVerification)) / (24 * 60 * 60 * 1000));
              localStorage.setItem(OFFLINE_DAYS_KEY, daysOffline.toString());
              
              if (daysOffline >= 60) {
                  return { valid: false, message: 'License requires internet connection. You have been offline for 60+ days. Please connect to the internet or scan QR code to verify.' };
              }
          }
          return { valid: true }; // Allow offline use if within 60 days
      }

      const targetUrl = baseUrl && baseUrl.startsWith('http') ? baseUrl : DEFAULT_SCRIPT_URL;
      const verifyUrl = `${targetUrl}/check?key=${encodeURIComponent(license.key)}&hwId=${encodeURIComponent(license.hwId)}`;
      
      try {
          const response = await fetch(verifyUrl, {
              method: 'GET',
              headers: { 'Accept': 'application/json' }
          });
          
          if (!response.ok) {
              // Server error - don't invalidate license, allow offline use
              const lastVerification = localStorage.getItem(LAST_VERIFICATION_KEY);
              if (lastVerification) {
                  const daysOffline = Math.floor((Date.now() - parseInt(lastVerification)) / (24 * 60 * 60 * 1000));
                  if (daysOffline < 60) {
                      return { valid: true };
                  }
              }
              // If no previous verification, allow use (server might be down)
              return { valid: true };
          }

          const data = await response.json();
          localStorage.setItem(LAST_VERIFICATION_KEY, Date.now().toString());
          localStorage.removeItem(OFFLINE_DAYS_KEY);

          // Extract expiry date from server response (check validUntil, validFrom+duration, or expiryDate)
          let expiryDate: number | undefined;
          if (data.validUntil) {
              // validUntil is a timestamp - convert to milliseconds if needed
              expiryDate = typeof data.validUntil === 'number' 
                  ? (data.validUntil > 1e12 ? data.validUntil : data.validUntil * 1000)
                  : new Date(data.validUntil).getTime();
          } else if (data.validFrom && data.duration) {
              // Calculate from validFrom + duration (in days)
              const validFromTime = typeof data.validFrom === 'number'
                  ? (data.validFrom > 1e12 ? data.validFrom : data.validFrom * 1000)
                  : new Date(data.validFrom).getTime();
              expiryDate = validFromTime + (data.duration * 24 * 60 * 60 * 1000);
          } else if (data.expiryDate) {
              expiryDate = typeof data.expiryDate === 'number'
                  ? (data.expiryDate > 1e12 ? data.expiryDate : data.expiryDate * 1000)
                  : new Date(data.expiryDate).getTime();
          }
          
          // Update stored license if expiry date was found
          if (expiryDate) {
              const storedLicense = LicenseManager.getStoredLicense();
              if (storedLicense && storedLicense.expiryDate !== expiryDate) {
                  storedLicense.expiryDate = expiryDate;
                  LicenseManager.saveLicense(storedLicense);
              }
          }

          // Only invalidate if server explicitly says revoked or expired
          if (data.revoked === true) {
              return { valid: false, revoked: true, message: 'This license has been revoked. Please contact support.' };
          }

          if (data.expired === true) {
              return { valid: false, expired: true, message: 'This license has expired.' };
          }

          // If server doesn't explicitly say revoked/expired, license is valid
          return { valid: true, ...(expiryDate ? { expiryDate } : {}) };
      } catch (e: any) {
          // On error, allow offline use if within grace period
          const lastVerification = localStorage.getItem(LAST_VERIFICATION_KEY);
          if (lastVerification) {
              const daysOffline = Math.floor((Date.now() - parseInt(lastVerification)) / (24 * 60 * 60 * 1000));
              if (daysOffline < 60) {
                  return { valid: true };
              }
          }
          // If no previous verification or within grace period, allow use
          return { valid: true };
      }
  },

  // Sync print count to server
  syncPrintCount: async (
      license: LicenseInfo,
      printCount: number,
      baseUrl?: string
  ): Promise<boolean> => {
      if (!navigator.onLine) return false;

      const lastSync = localStorage.getItem(LAST_PRINT_COUNT_SYNC_KEY);
      const oneDay = 24 * 60 * 60 * 1000;
      
      // Only sync once per day
      if (lastSync && (Date.now() - parseInt(lastSync)) < oneDay) {
          return true;
      }

      const targetUrl = baseUrl && baseUrl.startsWith('http') ? baseUrl : DEFAULT_SCRIPT_URL;
      const syncUrl = `${targetUrl}/printcount?key=${encodeURIComponent(license.key)}&hwId=${encodeURIComponent(license.hwId)}&count=${printCount}`;
      
      try {
          const response = await fetch(syncUrl, {
              method: 'POST',
              headers: { 'Accept': 'application/json' }
          });
          
          if (response.ok) {
              localStorage.setItem(LAST_PRINT_COUNT_SYNC_KEY, Date.now().toString());
              return true;
          }
          return false;
      } catch (e) {
          return false;
      }
  },

  // Generate QR code for offline verification
  generateVerificationQR: (
      license: LicenseInfo,
      baseUrl?: string
  ): string => {
      // QR codes use the QR base URL, not the API URL
      const targetUrl = baseUrl && baseUrl.startsWith('http') ? baseUrl : DEFAULT_QR_BASE_URL;
      return `${targetUrl}/verify-offline?key=${encodeURIComponent(license.key)}&hwId=${encodeURIComponent(license.hwId)}&timestamp=${Date.now()}`;
  }
};

// Helper for browser-only dev (Electron uses Node Crypto via IPC)
async function sha256Browser(message: string) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex.toUpperCase();
}

// Expose LicenseManager globally for debugging (ONLY in development mode)
// In production builds, this will be removed/minified
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    (window as any).LicenseManager = LicenseManager;
    (window as any).clearTrial = () => {
        LicenseManager.clearTrialFlag();
        LicenseManager.clear(); // Also clear the license data
        console.log('Trial and license cleared! Refresh the page.');
    };
    
    // Test function to verify server-side trial validation
    (window as any).testTrialValidation = async () => {
        console.log('🧪 Testing Trial Validation...\n');
        
        const testDeviceId = 'TEST-DEVICE-' + Date.now();
        const testDetails = {
            name: 'Test User',
            company: 'Test Company',
            address: '123 Test St',
            email: 'test@example.com',
            phone: '1234567890',
            platform: 'test'
        };
        
        try {
            // Test 1: First trial registration (should succeed)
            console.log('Test 1: Registering first trial with device ID:', testDeviceId);
            const firstTrial = await LicenseManager.activateTrial(testDeviceId, testDetails);
            console.log('✅ First trial registered successfully:', firstTrial);
            
            // Clear client-side flag to simulate bypass attempt
            LicenseManager.clearTrialFlag();
            console.log('\n⚠️ Cleared client-side trial flag (simulating bypass attempt)');
            
            // Test 2: Try to register again with same device ID (should fail on server)
            console.log('\nTest 2: Attempting to register trial again with same device ID...');
            try {
                const secondTrial = await LicenseManager.activateTrial(testDeviceId, testDetails);
                console.error('❌ SECURITY ISSUE: Server allowed duplicate trial!', secondTrial);
                console.error('   The server should have rejected this request.');
                return false;
            } catch (error: any) {
                if (error.message.includes('already used') || error.message.includes('already registered') || error.message.includes('duplicate')) {
                    console.log('✅ Server correctly rejected duplicate trial:', error.message);
                    return true;
                } else {
                    console.warn('⚠️ Server rejected, but unclear if it was due to duplicate device ID:', error.message);
                    return false;
                }
            }
        } catch (error: any) {
            console.error('❌ Test failed:', error.message);
            return false;
        }
    };
    
    console.warn('⚠️ Development mode: clearTrial() and testTrialValidation() are available. These will NOT be available in production builds.');
}
